import { HistoriqueEvent } from '../../types';

// Point d'acces UNIQUE au flux d'historique de l'operation. Le backend ne fournit
// (pour l'instant) qu'un `prefix` textuel comme marqueur d'evenement : on le centralise
// ici derriere des selecteurs types et des constantes nommees, au lieu de disséminer des
// comparaisons de chaines dans les composants (anti-pattern legacy). Le jour ou le backend
// expose un type d'evenement, seul ce module change.
export const HISTORIQUE_PREFIX = {
  CANDIDATURE: 'Candidature',
  DECHIFFRAGE: 'Dechiffrage',
  CONSULTE: 'Consulte',
  CONSULTATION: 'Consultation',
  VALIDATION: 'Validation',
  ANALYSE_CLOTUREE: 'Analyse clôturée',
  NOTIFICATION: 'Notification',
  DEMANDE_SIGNATURE: 'Demande de signature',
  SIGNATURE_ACHETEUR: 'Signature acheteur',
  PROLONGATION: 'Prolongation',
} as const;

// Historique des prolongations du delai de reception des offres (plus recente en premier).
// Source : evenements journalises par le core sous le prefix 'Prolongation' (date + operateur + message).
export interface ProlongationInfo {
  date: string | null;
  operateur: string | null;
  message: string | null;
}
export const getProlongations = (historique?: HistoriqueEvent[]): ProlongationInfo[] =>
  (historique || [])
    .filter((h) => h.prefix === HISTORIQUE_PREFIX.PROLONGATION)
    .map((h) => ({ date: h.created_at ?? null, operateur: h.operateur ?? null, message: h.message ?? null }))
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));

const findByPrefix = (
  historique: HistoriqueEvent[] | undefined,
  prefixes: readonly string[],
): HistoriqueEvent | undefined =>
  (historique || []).find((h) => h.prefix !== undefined && prefixes.includes(h.prefix));

export interface DechiffrageInfo {
  date: string | null;
  operateur: string | null;
}

// Info de dechiffrage des plis (date + operateur) ; absente tant que les plis sont scelles.
export const getDechiffrageInfo = (historique?: HistoriqueEvent[]): DechiffrageInfo => {
  const entry = findByPrefix(historique, [HISTORIQUE_PREFIX.DECHIFFRAGE]);
  return { date: entry?.created_at ?? null, operateur: entry?.operateur ?? null };
};

// Info de cloture d'analyse (date + operateur). Le backend journalise la cloture MS sous le
// prefix 'Analyse clôturée' (les BC utilisent 'Validation') : on accepte les deux pour que le
// recap « clôturée le … par … » s'affiche. null tant que l'analyse n'est pas cloturee.
export const getClotureAnalyseInfo = (historique?: HistoriqueEvent[]): DechiffrageInfo => {
  const entry = findByPrefix(historique, [HISTORIQUE_PREFIX.VALIDATION, HISTORIQUE_PREFIX.ANALYSE_CLOTUREE]);
  return { date: entry?.created_at ?? null, operateur: entry?.operateur ?? null };
};

// Info de lancement de la consultation (date + operateur=personne qui lance). Source : evenement
// journalise par lancerOperation cote core sous le prefix 'Consultation'. date null tant que la
// consultation n'est pas lancee (operation au brouillon). Alimente le recap « lancee le … par … ».
export const getLancementConsultationInfo = (historique?: HistoriqueEvent[]): DechiffrageInfo => {
  const entry = findByPrefix(historique, [HISTORIQUE_PREFIX.CONSULTATION]);
  return { date: entry?.created_at ?? null, operateur: entry?.operateur ?? null };
};

// Info de signature acheteur (date + operateur=organisme) depuis l'evenement 'Signature acheteur' ;
// null tant que le marche n'est pas signe. Source du « notifie au titulaire le {date} ».
export const getSignatureAcheteurInfo = (historique?: HistoriqueEvent[]): DechiffrageInfo => {
  const entry = findByPrefix(historique, [HISTORIQUE_PREFIX.SIGNATURE_ACHETEUR]);
  return { date: entry?.created_at ?? null, operateur: entry?.operateur ?? null };
};

// Date de candidature d'une commande (offre recue), par destinataire.
export const getCandidatureDate = (
  historique: HistoriqueEvent[] | undefined,
  commandeId: number | string | undefined,
): string | null => {
  if (commandeId === undefined) return null;
  const entry = (historique || []).find(
    (h) =>
      h.prefix === HISTORIQUE_PREFIX.CANDIDATURE &&
      h.destinataire !== null &&
      h.destinataire !== undefined &&
      String(h.destinataire) === String(commandeId),
  );
  return entry?.created_at ?? null;
};

// Nombre de plis recus = prestataires DISTINCTS ayant depose une offre (evenement Candidature).
// Disponible des la consultation : l'evenement est journalise a la soumission, avant dechiffrage,
// donc l'acheteur voit la progression des depots meme plis scelles.
export const countPlisRecus = (historique?: HistoriqueEvent[]): number =>
  new Set(
    (historique || [])
      .filter(
        (h) =>
          h.prefix === HISTORIQUE_PREFIX.CANDIDATURE &&
          h.destinataire !== null &&
          h.destinataire !== undefined,
      )
      .map((h) => String(h.destinataire)),
  ).size;

// L'analyse a-t-elle ete lancee (plis consultes) ? Marqueur d'historique consolide.
export const isAnalyzed = (historique?: HistoriqueEvent[]): boolean =>
  findByPrefix(historique, [HISTORIQUE_PREFIX.CONSULTE, HISTORIQUE_PREFIX.CONSULTATION]) !== undefined;

// L'analyse a-t-elle ete validee/cloturee (evenement 'Validation') ? Consomme par le gating
// des onglets et de la messagerie (source unique, plus de comparaison de chaine disséminee).
export const isAnalyseValidee = (historique?: HistoriqueEvent[]): boolean =>
  findByPrefix(historique, [HISTORIQUE_PREFIX.VALIDATION]) !== undefined;

// Date de notification d'une commande (retenu/non-retenu), par destinataire.
export const getNotificationDate = (
  historique: HistoriqueEvent[] | undefined,
  commandeId: number | string | undefined,
): string | null => {
  if (commandeId === undefined) return null;
  const entry = (historique || []).find(
    (h) => h.prefix === HISTORIQUE_PREFIX.NOTIFICATION && String(h.destinataire) === String(commandeId),
  );
  return entry?.created_at ?? null;
};

// Demandes de signature (acheteur simple -> signataire) presentes dans l'historique.
export const getDemandesSignature = (historique?: HistoriqueEvent[]): HistoriqueEvent[] =>
  (historique || []).filter((h) => h.prefix === HISTORIQUE_PREFIX.DEMANDE_SIGNATURE);

// IP de candidature par destinataire (tracabilite), depuis l'historique.
export const getIpParCommande = (historique?: HistoriqueEvent[]): Record<string, string> => {
  const map: Record<string, string> = {};
  (historique || []).forEach((h) => {
    if (
      h.prefix === HISTORIQUE_PREFIX.CANDIDATURE &&
      h.destinataire !== null &&
      h.destinataire !== undefined &&
      h.ip_address
    ) {
      map[String(h.destinataire)] = h.ip_address;
    }
  });
  return map;
};
